import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { palette } from 'styled-theme';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 10px 25px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
  border: 1px solid ${palette('border', 1)};
  box-shadow: 4px 0 12px rgba(99, 102, 241, 0.15);

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 600px) {
    width: 100%;
    max-height: 85vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  flex-shrink: 0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.2px;
  }

  @media (max-width: 600px) {
    padding: 16px 20px;

    h2 {
      font-size: 16px;
    }
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
  border-radius: 6px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: #f3f4f6;
    color: #f55a6e;
  }

  &:active {
    background: #e5e7eb;
  }

  &:focus-visible {
    outline: 2px solid #0066cc;
    outline-offset: 2px;
  }

  @media (max-width: 600px) {
    width: 32px;
    height: 32px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  color: #374151;

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  flex-shrink: 0;

  @media (max-width: 600px) {
    padding: 12px 20px;
    gap: 8px;

    button {
      flex: 1;
    }
  }
`;

const AdminModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = '500px',
  showCloseButton = true,
}) => {
  const modalRef = useRef(null);

  if (!isOpen) return null;

  return (
    <Backdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer
        ref={modalRef}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="hide-scroll-bar"
      >
        <ModalHeader>
          <h2 id="modal-title">{title}</h2>
          {showCloseButton && (
            <CloseButton onClick={onClose} aria-label="Close modal" type="button">
              <X />
            </CloseButton>
          )}
        </ModalHeader>

        <ModalBody>{children}</ModalBody>

        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContainer>
    </Backdrop>
  );
};

export default AdminModal;
